// src/v2/components/SearchInput.jsx
//
// Shared search field used across list-view toolbars (Offices, Leases, …).
// Renders a text input with a leading magnifying-glass icon and an inline ×
// "Clear search" button that appears only when the field has text. The ×
// clears the search text in place (does not touch filters) and refocuses
// the input.
//
// Props:
//   value        — controlled search text
//   onChange     — (nextValue: string) => void
//   placeholder  — input placeholder text
//   testId       — data-testid for the <input> element
//   width        — optional CSS width/minWidth override for the input
//   style        — optional style overrides merged onto the input

import React, { useRef, useState } from "react";
import { T } from "../../app/tokens.js";

export function SearchInput({
  value,
  onChange,
  placeholder = "Search…",
  testId = "v2-search-input",
  width,
  style,
}) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  function handleClear() {
    onChange("");
    inputRef.current?.focus();
  }

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <input
        ref={inputRef}
        type="text"
        data-testid={testId}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          border:       "1px solid var(--v2-line)",
          borderRadius: 9,
          padding:      value ? "8px 30px 8px 32px" : "8px 12px 8px 32px",
          fontSize:     T.fsSm5,
          color:        "var(--v2-dark)",
          background:   "var(--v2-card)",
          outline:      "none",
          fontFamily:   "inherit",
          width:        width ?? 200,
          borderColor:  focused ? "var(--v2-infocus-border)" : "var(--v2-line)",
          boxShadow:    focused ? "var(--v2-shadow-ring)" : "none",
          ...style,
        }}
      />
      <svg
        width="13" height="13" viewBox="0 0 24 24"
        fill="none" stroke="var(--v2-light)"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          data-testid={`${testId}-clear`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
          style={{
            position:     "absolute",
            right:        8,
            top:          "50%",
            transform:    "translateY(-50%)",
            border:       "none",
            background:   "none",
            padding:      2,
            display:      "flex",
            alignItems:   "center",
            justifyContent: "center",
            cursor:       "pointer",
            color:        "var(--v2-light)",
            borderRadius: 4,
          }}
        >
          <svg
            width="12" height="12" viewBox="0 0 24 24"
            fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}
